import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Button,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FileUpload from '../../../helper/FileUpload';
import uploadIcon from '/src/assets/images/application/folder-file-up.svg?url';
import { SPACING, RADIUS } from '../../../theme/theme';
import { useImageLightbox } from '../../Feedback/ImageLightbox';

export interface ExistingImage {
  id: number;
  url: string;
  /** Uploaded against an approved listing and not on the store until reviewed. */
  is_pending?: boolean;
}

const THUMB_WIDTH = 150;
const THUMB_HEIGHT = 229;

const thumbnailSx = {
  position: 'relative',
  height: `${THUMB_HEIGHT}px`,
  width: `${THUMB_WIDTH}px`,
  flexShrink: 0,
  borderRadius: `${RADIUS.md}px`,
  overflow: 'hidden',
  border: '1px solid #E5E5E8',
  backgroundColor: '#F2F2F5',
  // Lift the tile and reveal its delete button on hover.
  transition: 'box-shadow .2s ease, transform .2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,.14)',
    transform: 'translateY(-2px)',
  },
  '&:hover .thumb-actions': { opacity: 1 },
} as const;

const imageSx = {
  display: 'block',
  height: '100%',
  width: '100%',
  objectFit: 'cover',
} as const;

const deleteButtonSx = {
  position: 'absolute',
  top: SPACING.xs,
  right: SPACING.xs,
  width: 28,
  height: 28,
  backgroundColor: 'rgba(255,255,255,.92)',
  color: '#D32F2F',
  boxShadow: '0 1px 4px rgba(0,0,0,.2)',
  // Always visible on touch devices, which have no hover.
  opacity: 0,
  transition: 'opacity .2s ease, background-color .2s ease',
  '@media (hover: none)': { opacity: 1 },
  '&:hover': { backgroundColor: '#fff' },
} as const;

/**
 * A single screenshot tile. Keeps its own loading flag so a slow image shows
 * a skeleton in place instead of an empty bordered box that pops in.
 */
const Thumbnail: React.FC<{
  src: string;
  alt: string;
  busy?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  // Uploaded but not reviewed yet, so not on the store. Marked rather than
  // hidden: the publisher needs to see that it arrived.
  pending?: boolean;
  innerProps?: Record<string, unknown>;
  // Applied to the <img> rather than the tile, so the delete button on top
  // keeps its own click handler.
  previewProps?: Record<string, unknown>;
}> = ({
  src,
  alt,
  busy = false,
  onDelete,
  deleteDisabled,
  pending = false,
  innerProps,
  previewProps,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // A cached image can finish before React attaches onLoad, which would leave
  // the skeleton up forever. Only adopt a *successful* decode here: `complete`
  // is also true for an image that has not started loading, so treating
  // !naturalWidth as failure would flag every image as broken on first render.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <Box
      sx={{
        ...thumbnailSx,
        ...(pending ? { outline: '2px solid #F0AD4E', outlineOffset: '-2px' } : {}),
      }}
      {...innerProps}
    >
      {!loaded && !failed && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height="100%"
          sx={{ position: 'absolute', inset: 0 }}
        />
      )}

      {pending && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            insetInlineStart: 4,
            zIndex: 2,
            backgroundColor: '#FEF9C3',
            color: '#A16207',
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '10px',
            fontFamily: 'IRANYekanXFaNum',
            pointerEvents: 'none',
          }}
        >
          در انتظار بررسی
        </Box>
      )}

      {failed ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#A6AAB5',
            fontSize: 12,
            fontFamily: 'IRANYekanXFaNum',
            textAlign: 'center',
            padding: `${SPACING.sm}px`,
          }}
        >
          تصویر بارگذاری نشد
        </Box>
      ) : (
        <Box
          component="img"
          ref={imgRef}
          src={src}
          alt={alt}
          // No loading="lazy" here: inside a horizontally scrolled strip the
          // browser defers off-screen images indefinitely, so their skeletons
          // never resolve.
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          {...previewProps}
          sx={{
            ...imageSx,
            // Fade in once decoded so the swap from skeleton isn't a jump.
            opacity: loaded ? 1 : 0,
            transition: 'opacity .3s ease',
            cursor: previewProps ? 'zoom-in' : undefined,
          }}
        />
      )}

      {onDelete &&
        (busy ? (
          <Box
            sx={{
              ...deleteButtonSx,
              opacity: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
            }}
          >
            <CircularProgress size={16} sx={{ color: '#D32F2F' }} />
          </Box>
        ) : (
          <IconButton
            className="thumb-actions"
            size="small"
            sx={deleteButtonSx}
            disabled={deleteDisabled}
            onClick={onDelete}
            aria-label={`حذف ${alt}`}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ))}
    </Box>
  );
};

interface ImageEditContainerProps {
  onImageUpload: (files: File[]) => void;
  onRemoveImage: (index: number) => void;
  /**
   * Screenshots already stored against the application. These are shown from
   * their url rather than downloaded back into `File`s: they are served by
   * imgproxy, which sends no CORS headers, so `fetch()` on them is blocked
   * even though an `<img>` renders them fine.
   */
  existingImages?: ExistingImage[];
  /** Deletes a stored screenshot. Applies immediately and cannot be undone. */
  onDeleteExisting?: (id: number) => Promise<void>;
}

const ImageEditContainer: React.FC<ImageEditContainerProps> = ({
  onImageUpload,
  onRemoveImage,
  existingImages = [],
  onDeleteExisting,
}) => {
  const lightbox = useImageLightbox();
  // Only newly picked files live here. Existing screenshots stay identified by
  // their id, so an edit that touches nothing re-uploads nothing.
  const [files, setFiles] = useState<File[]>([]);
  const [pendingDelete, setPendingDelete] = useState<ExistingImage | null>(
    null,
  );
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // One object url per File, revoked on unmount. Building them inline in
  // render mints a new url every pass and leaks each one.
  const previewUrlsRef = useRef(new Map<File, string>());

  const previewUrl = (file: File): string => {
    const cached = previewUrlsRef.current.get(file);

    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    previewUrlsRef.current.set(file, url);

    return url;
  };

  useEffect(() => {
    const urls = previewUrlsRef.current;

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const handleFileUpload = (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) =>
      ['image/png', 'image/jpeg'].includes(file.type),
    );

    if (validFiles.length === 0) {
      return;
    }

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, ...validFiles];
      onImageUpload(updatedFiles);
      return updatedFiles;
    });
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => {
      const removed = prevFiles[index];
      const updatedFiles = prevFiles.filter((_, i) => i !== index);

      const url = previewUrlsRef.current.get(removed);
      if (url) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(removed);
      }

      onImageUpload(updatedFiles);
      onRemoveImage(index);
      return updatedFiles;
    });
  };

  const confirmDeleteExisting = async () => {
    if (!pendingDelete || !onDeleteExisting) {
      return;
    }

    const { id } = pendingDelete;

    setDeletingId(id);
    setPendingDelete(null);

    try {
      await onDeleteExisting(id);
    } finally {
      setDeletingId(null);
    }
  };

  // Only the newly picked files are reorderable; the stored ones keep the
  // order they already have on the server.
  const handleDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    const reorderedFiles = Array.from(files);
    const [removed] = reorderedFiles.splice(source.index, 1);
    reorderedFiles.splice(destination.index, 0, removed);

    setFiles(reorderedFiles);
    onImageUpload(reorderedFiles);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${SPACING.sm}px`,
        width: '100%',
        padding: `${SPACING.sm}px`,
        border: '1px solid #E5E5E8',
        borderRadius: `${RADIUS.md}px`,
        backgroundColor: '#FBFBFC',
        boxSizing: 'border-box',
      }}
    >
      {/* Upload tile stays pinned; only the thumbnails scroll. */}
      <Box sx={{ flexShrink: 0 }}>
        <FileUpload
          height={`${THUMB_HEIGHT}px`}
          width={`${THUMB_WIDTH}px`}
          direction="rtl"
          flexDirection="column"
          showFileInfo={false}
          onFilesSelected={handleFileUpload}
          imageSrc={uploadIcon}
          backgroundColor="#F2F2F5"
          uploadText="افزودن تصویر"
          acceptedFileTypes={['.png', '.jpg', '.jpeg']}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: `${SPACING.sm}px`,
          overflowX: 'auto',
          // Room for the tile's hover lift and its shadow.
          padding: `${SPACING.xs}px`,
          width: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        {existingImages.length === 0 && files.length === 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: `${THUMB_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#A6AAB5',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 13,
            }}
          >
            هنوز تصویری اضافه نشده است
          </Box>
        )}

        {existingImages.map((image) => (
          <Thumbnail
            key={`existing-${image.id}`}
            src={image.url}
            alt={`تصویر ${image.id}`}
            busy={deletingId === image.id}
            deleteDisabled={deletingId !== null}
            pending={image.is_pending}
            onDelete={() => setPendingDelete(image)}
            previewProps={lightbox.triggerProps(
              image.url,
              `تصویر ${image.id}`,
            )}
          />
        ))}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable" direction="horizontal">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                {files.map((file, index) => (
                  <Draggable
                    key={`${file.name}-${file.size}-${index}`}
                    draggableId={`${file.name}-${file.size}-${index}`}
                    index={index}
                  >
                    {(dragProvided) => (
                      <Thumbnail
                        src={previewUrl(file)}
                        alt={file.name}
                        onDelete={() => handleRemoveFile(index)}
                        innerProps={{
                          ref: dragProvided.innerRef,
                          ...dragProvided.draggableProps,
                          ...dragProvided.dragHandleProps,
                        }}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        dir="rtl"
      >
        <DialogTitle sx={{ fontFamily: 'IRANYekanXFaNum' }}>
          حذف تصویر
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'IRANYekanXFaNum' }}>
            این تصویر همین حالا از سرور پاک می‌شود و قابل بازگشت نیست. مطمئن
            هستید؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            sx={{ fontFamily: 'IRANYekanXFaNum' }}
          >
            انصراف
          </Button>
          <Button
            onClick={confirmDeleteExisting}
            color="error"
            variant="contained"
            sx={{ fontFamily: 'IRANYekanXFaNum' }}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      {lightbox.element}
    </Box>
  );
};

export default ImageEditContainer;
